import type { NonsoleReadinessReport, GateState } from '@playcastdotio/NonsoleReadinessTypes';

export type GateIssueCategory =
  | 'silent-skip'
  | 'named-confirm'
  | 'user-action'
  | 'please-wait'
  | 'contact-support'
  | 'ignore';

export type UserActionKind =
  | 'plug-in'
  | 'sign-out-user'
  | 'reconnect-drive'
  | 'unplug-controllers'
  | 'enable-antivirus'
  | 'reboot';

export interface GateIssue {
  gateId: string;
  category: GateIssueCategory;
  actionKind?: UserActionKind;
  label: string;
  detail: string;
}

export interface CategorizedGates {
  appsHolding: string[];
  userActions: GateIssue[];
  pleaseWait: GateIssue[];
  contactSupport: GateIssue[];
  clearToEnter: boolean;
}

const SILENT_SKIP_IDS = new Set(['IDLE_TIMER', 'POST_RESUME_GRACE']);
const IGNORE_IDS = new Set(['NONSOLE_READINESS_SELF_HEALTH']);
const NAMED_CONFIRM_IDS = new Set(['PRESENCE_HOLD']);
const PLEASE_WAIT_IDS = new Set([
  'DEFERRED_INSTALL',
  'INSTALL_STATE',
  'INSTALL_MANAGER_BUSY',
  'INSTALLER_RUNNING',
  'DRIVER_HELPER_BUSY',
]);
const CONTACT_SUPPORT_IDS = new Set([
  'ROLLOUT_ENABLED',
  'SESSION_RETENTION_POLICY',
  'PERSISTENT_ACCOUNT',
  'HOST_EXE',
  'INPUT_DRIVERS_HEALTHY',
  'PLAYCAST_DRIVERS_HEALTHY',
  'SECURITY_SIGNATURE',
  'KERNEL_DEBUGGER',
  'VM_ENVIRONMENT',
]);

const USER_ACTION_MAP: Record<string, { kind: UserActionKind; label: string }> = {
  MIGRATION_REBOOT: { kind: 'reboot', label: 'Restart to finish setup' },
  AC_POWER: { kind: 'plug-in', label: 'Plug in your charger' },
  SESSION_COUNT: { kind: 'sign-out-user', label: 'Sign out the other Windows user' },
  STORAGE_DRIVE: { kind: 'reconnect-drive', label: 'Reconnect your games drive' },
  VIRTUAL_CONTROLLER: { kind: 'unplug-controllers', label: 'Unplug extra controllers' },
  ANTIVIRUS: { kind: 'enable-antivirus', label: 'Turn on Windows Security' },
};

function getCategory(gateId: string): GateIssueCategory {
  if (SILENT_SKIP_IDS.has(gateId)) return 'silent-skip';
  if (IGNORE_IDS.has(gateId)) return 'ignore';
  if (NAMED_CONFIRM_IDS.has(gateId)) return 'named-confirm';
  if (PLEASE_WAIT_IDS.has(gateId)) return 'please-wait';
  if (CONTACT_SUPPORT_IDS.has(gateId)) return 'contact-support';
  if (gateId in USER_ACTION_MAP) return 'user-action';
  return 'contact-support';
}

function buildIssue(gate: GateState): GateIssue {
  const category = getCategory(gate.id);

  if (category === 'user-action') {
    const action = USER_ACTION_MAP[gate.id];
    return {
      gateId: gate.id,
      category,
      actionKind: action.kind,
      label: action.label,
      detail: gate.reasonMessage ?? gate.displayName,
    };
  }

  if (category === 'please-wait') {
    return {
      gateId: gate.id,
      category,
      label: gate.displayName,
      detail: gate.reasonMessage ?? 'Playcast is still setting up.',
    };
  }

  return {
    gateId: gate.id,
    category,
    label: gate.displayName,
    detail: gate.reasonMessage ?? '',
  };
}

export function categorizeGates(report: NonsoleReadinessReport): CategorizedGates {
  const failingBlockers = report.gates.filter(
    (g) => g.severity === 'blocking' && !g.passed,
  );

  const appsHolding: string[] = [];
  const userActions: GateIssue[] = [];
  const pleaseWait: GateIssue[] = [];
  const contactSupport: GateIssue[] = [];

  for (const gate of failingBlockers) {
    const category = getCategory(gate.id);

    if (category === 'silent-skip' || category === 'ignore') continue;

    if (category === 'named-confirm') {
      const appHolds = report.idleBlockers?.appHolds ?? [];
      const relevantHolds = appHolds.filter((h) => !h.ignoredForPresence);
      const names = [...new Set(relevantHolds.map((h) => h.processName))];
      if (names.length > 0) {
        appsHolding.push(...names);
      } else {
        appsHolding.push('An app is holding the screen');
      }
      continue;
    }

    const issue = buildIssue(gate);

    switch (category) {
      case 'user-action':
        userActions.push(issue);
        break;
      case 'please-wait':
        pleaseWait.push(issue);
        break;
      case 'contact-support':
        contactSupport.push(issue);
        break;
    }
  }

  const clearToEnter =
    appsHolding.length === 0 &&
    userActions.length === 0 &&
    pleaseWait.length === 0 &&
    contactSupport.length === 0;

  return { appsHolding, userActions, pleaseWait, contactSupport, clearToEnter };
}
